'use client'
import { ButtonRoundStyle } from '@/components'
import { Form } from '@/components/form/form-provider'
import { OTPCountDown } from '@/features/auth/components'
import { confirmOtpForPasswordReset, requestPasswordResetEmail } from '@/features/auth/services'
import yup from '@/utils/yup-global'
import { registerSchema } from '@/validations'
import { yupResolver } from '@hookform/resolvers/yup'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { Stack } from '@mui/material'
import { useTranslations } from 'next-intl'
import { useTransition } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

type Props = {
  email: string
  onNext?: (otpToken: string) => void
  onBack?: () => void
  onResendOtp?: () => void
}

export const ResetPasswordOtpForm = ({ email, onNext, onBack, onResendOtp }: Props) => {
  const [isPending, startTransition] = useTransition()
  const t = useTranslations('UserProfile')
  const method = useForm()
  const { handleSubmit } = method

  const onSubmit = (data: any) => {
    if (!data.otp) {
      toast.error('Vui lòng nhập mã OTP')
      return
    }

    startTransition(async () => {
      try {
        const result = await confirmOtpForPasswordReset(data.otp, email)

        if (result.status === 'error') {
          toast.error(result.message)
          return
        }

        toast.success(result.message)
        onNext?.(data.otp) // Trả về otpToken
      } catch (error) {
        console.error('OTP confirmation error:', error)
        toast.error('Có lỗi xảy ra khi xác minh mã OTP')
      }
    })
  }

  const handleOtpComplete = (otpValue: string) => {
    method.setValue('otp', otpValue)
    onSubmit({ otp: otpValue })
  }

  const handleResendOtp = async () => {
    try {
      const result = await requestPasswordResetEmail(email)

      if (result.status === 'error') {
        toast.error(result.message)
        return
      }

      toast.success('Mã xác minh mới đã được gửi')
    } catch (error) {
      console.error('Resend OTP error:', error)
      toast.error('Có lỗi xảy ra khi gửi lại mã OTP')
    }
  }

  return (
    <Form methods={method} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={2}>
        <OTPCountDown
          onSubmit={handleOtpComplete}
          align="center"
          subtitle={t.rich(
            'Vui lòng nhập đoạn mã gồm 6 chữ số vừa gửi để xác minh tài khoản vừa nhập là của bạn',
            {
              goal: email,
              bold: (chunks) => <strong>{chunks}</strong>,
            }
          )}
          onResendOTP={handleResendOtp}
        />

        <ButtonRoundStyle variant="contained" type="submit" disabled={isPending} loading={isPending}>
          {t('Xác minh tài khoản')}
        </ButtonRoundStyle>
        <ButtonRoundStyle variant="outlined" onClick={onBack} disabled={isPending}>
          <ArrowBackIcon /> {t('Quay về')}
        </ButtonRoundStyle>
      </Stack>
    </Form>
  )
}
